import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Search } from "lucide-react";
import api from "@/lib/api";

const EMPTY = { domain: "makeup", category: "lipstick", brand: "", name: "", color_hex: "#DDA7A5", price: 19.9, undertone: "neutral", affiliate_url: "" };

function StatCard({ label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-rose-100 p-5">
      <p className="text-xs uppercase tracking-widest text-charcoal/40">{label}</p>
      <p className="font-serif text-3xl mt-1">{value ?? "—"}</p>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState({});
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [domain, setDomain] = useState("");
  const [skip, setSkip] = useState(0);
  const [modal, setModal] = useState(null);
  const limit = 20;

  const loadStats = useCallback(() => api.get("/admin/stats").then(({ data }) => setStats(data)), []);
  const load = useCallback(() => {
    const params = new URLSearchParams({ limit, skip });
    if (q) params.set("q", q);
    if (domain) params.set("domain", domain);
    api.get(`/products?${params}`).then(({ data }) => { setItems(data.items); setTotal(data.total); });
  }, [q, domain, skip]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (modal.sku) {
        await api.put(`/admin/products/${modal.sku}`, modal);
        toast.success("Prodotto aggiornato");
      } else {
        await api.post("/admin/products", modal);
        toast.success("Prodotto creato");
      }
      setModal(null); load(); loadStats();
    } catch (e) { toast.error("Errore nel salvataggio"); }
  };

  const del = async (sku) => {
    if (!window.confirm("Eliminare questo prodotto?")) return;
    await api.delete(`/admin/products/${sku}`);
    toast.success("Prodotto eliminato"); load(); loadStats();
  };

  return (
    <div className="bg-surface min-h-screen">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-rose-500 font-semibold">Dashboard Amministrativa</p>
            <h1 className="mt-1 font-serif text-4xl">Gestione Velyrea</h1>
          </div>
          <button onClick={() => setModal({ ...EMPTY })} data-testid="admin-add-btn" className="inline-flex items-center gap-2 rounded-full bg-rose-400 hover:bg-rose-500 text-white font-semibold px-5 py-3 transition-colors"><Plus size={18} /> Nuovo prodotto</button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mt-8">
          <StatCard label="Prodotti" value={stats.products} />
          <StatCard label="Make-up" value={stats.makeup} />
          <StatCard label="Skincare" value={stats.skincare} />
          <StatCard label="Fashion" value={stats.fashion} />
          <StatCard label="Brand" value={stats.brands} />
          <StatCard label="Utenti" value={stats.users} />
          <StatCard label="Pagine SEO" value={stats.seo_pages} />
        </div>

        <div className="flex gap-3 mt-8 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
            <input value={q} onChange={(e) => { setSkip(0); setQ(e.target.value); }} placeholder="Cerca..." data-testid="admin-search" className="w-full pl-10 pr-4 py-2.5 rounded-full border border-rose-100 focus:outline-none" />
          </div>
          <select value={domain} onChange={(e) => { setSkip(0); setDomain(e.target.value); }} className="px-4 py-2.5 rounded-full border border-rose-100 bg-white">
            <option value="">Tutti i domini</option><option value="makeup">Make-up</option><option value="skincare">Skincare</option><option value="fashion">Fashion</option>
          </select>
        </div>

        <div className="bg-white rounded-2xl border border-rose-100 overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-products-table">
            <thead className="bg-surface text-charcoal/50 text-xs uppercase tracking-wider">
              <tr><th className="text-left p-3">Brand</th><th className="text-left p-3">Nome</th><th className="text-left p-3">Dominio</th><th className="text-left p-3">Prezzo</th><th className="text-right p-3">Azioni</th></tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.sku} className="border-t border-rose-50 hover:bg-rose-50/40" data-testid={`admin-row-${p.sku}`}>
                  <td className="p-3 font-medium">{p.brand}</td>
                  <td className="p-3">{p.name}</td>
                  <td className="p-3"><span className="text-xs bg-rose-50 rounded-full px-2 py-0.5">{p.domain}</span></td>
                  <td className="p-3">€{Number(p.price).toFixed(2)}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => setModal({ ...p })} data-testid={`admin-edit-${p.sku}`} className="p-2 hover:text-rose-500"><Pencil size={15} /></button>
                    <button onClick={() => del(p.sku)} data-testid={`admin-delete-${p.sku}`} className="p-2 hover:text-red-500"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-4 text-sm">
          <span className="text-charcoal/50">{total} prodotti totali</span>
          <div className="flex gap-2">
            <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} className="px-4 py-2 rounded-full border border-rose-100 disabled:opacity-40">← Prec</button>
            <button disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)} className="px-4 py-2 rounded-full border border-rose-100 disabled:opacity-40">Succ →</button>
          </div>
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-charcoal/40 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-modal">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-2xl">{modal.sku ? "Modifica prodotto" : "Nuovo prodotto"}</h2>
              <button onClick={() => setModal(null)}><X /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm">Dominio<select value={modal.domain} onChange={(e) => setModal({ ...modal, domain: e.target.value })} className="mt-1 w-full border border-rose-100 rounded-lg p-2"><option value="makeup">Make-up</option><option value="skincare">Skincare</option><option value="fashion">Fashion</option></select></label>
              <label className="text-sm">Categoria<input value={modal.category} onChange={(e) => setModal({ ...modal, category: e.target.value })} className="mt-1 w-full border border-rose-100 rounded-lg p-2" /></label>
              <label className="text-sm">Brand<input value={modal.brand} onChange={(e) => setModal({ ...modal, brand: e.target.value })} data-testid="modal-brand" className="mt-1 w-full border border-rose-100 rounded-lg p-2" /></label>
              <label className="text-sm">Nome<input value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })} data-testid="modal-name" className="mt-1 w-full border border-rose-100 rounded-lg p-2" /></label>
              <label className="text-sm">Prezzo<input type="number" value={modal.price} onChange={(e) => setModal({ ...modal, price: parseFloat(e.target.value) })} className="mt-1 w-full border border-rose-100 rounded-lg p-2" /></label>
              <label className="text-sm">Colore<input type="color" value={modal.color_hex || "#DDA7A5"} onChange={(e) => setModal({ ...modal, color_hex: e.target.value })} className="mt-1 w-full border border-rose-100 rounded-lg p-1 h-10" /></label>
              <label className="text-sm col-span-2">URL affiliato<input value={modal.affiliate_url || ""} onChange={(e) => setModal({ ...modal, affiliate_url: e.target.value })} className="mt-1 w-full border border-rose-100 rounded-lg p-2" /></label>
            </div>
            <button onClick={save} data-testid="modal-save" className="mt-5 w-full rounded-full bg-rose-400 hover:bg-rose-500 text-white font-semibold py-3 transition-colors">Salva</button>
          </div>
        </div>
      )}
    </div>
  );
}
